// Defines what output variables each node type exposes.
// `alias` is used as the prefix in variable references: {{alias.key}}

import { WALLETTWO_EVENT_FIELDS } from './fields';

// Base variables every WalletTwo event always provides (from user object)
const WALLETTWO_BASE_VARS = [
  { key: 'timestamp', label: 'Timestamp' },
  { key: 'type', label: 'Event Type' },
  { key: 'user.id', label: 'User ID' },
  { key: 'user.name', label: 'User Name' },
  { key: 'user.email', label: 'User Email' },
  { key: 'user.wallet', label: 'User Wallet' },
];

// Extra output variables per event (beyond the base + config fields)
const WALLETTWO_EXTRA_VARS = {
  userRegistered: [],
  userLoggedIn: [],
  walletCreated: [
    { key: 'user.walletSecured', label: 'Wallet Secured' },
  ],
  walletRecovered: [
    { key: 'user.walletSecured', label: 'Wallet Secured' },
  ],
  userJoinedCompany: [
    { key: 'user.role', label: 'Role' },
  ],
  transactionSponsored: [
    { key: 'session.txHash', label: 'Transaction Hash' },
    { key: 'session.amount', label: 'Amount' },
    { key: 'session.network.name', label: 'Network Name' },
    { key: 'session.network.chainId', label: 'Chain ID' },
    { key: 'session.contract.name', label: 'Contract Name' },
    { key: 'session.contract.symbol', label: 'Contract Symbol' },
    { key: 'session.contract.address', label: 'Contract Address' },
  ],
  rampSessionCompleted: [
    { key: 'session.id', label: 'Session ID' },
    { key: 'session.amount', label: 'Amount' },
    { key: 'session.amountType', label: 'Amount Type' },
    { key: 'session.paymentMethod', label: 'Payment Method' },
    { key: 'session.externalId', label: 'External ID' },
    { key: 'session.createdAt', label: 'Session Created At' },
    { key: 'session.updatedAt', label: 'Session Updated At' },
    { key: 'session.currency.id', label: 'Currency ID' },
    { key: 'session.currency.name', label: 'Currency Name' },
    { key: 'session.currency.symbol', label: 'Currency Symbol' },
    { key: 'session.network.name', label: 'Network Name' },
    { key: 'session.network.symbol', label: 'Network Symbol' },
    { key: 'session.network.chainId', label: 'Chain ID' },
    { key: 'session.network.networkType', label: 'Network Type' },
    { key: 'session.network.explorerUrl', label: 'Network Explorer URL' },
    { key: 'session.contract.name', label: 'Token Name' },
    { key: 'session.contract.symbol', label: 'Token Symbol' },
    { key: 'session.contract.address', label: 'Token Address' },
    { key: 'session.contract.decimals', label: 'Token Decimals' },
    { key: 'session.contract.logo', label: 'Token Logo' },
    { key: 'session.checkoutContract.name', label: 'Payment Token Name' },
    { key: 'session.checkoutContract.symbol', label: 'Payment Token Symbol' },
    { key: 'session.checkoutContract.address', label: 'Payment Token Address' },
    { key: 'session.checkoutContract.decimals', label: 'Payment Token Decimals' },
    { key: 'session.checkoutContract.logo', label: 'Payment Token Logo' },
    { key: 'session.quote.source', label: 'Quote Source' },
    { key: 'session.quote.token', label: 'Quote Token' },
    { key: 'session.quote.currency', label: 'Quote Currency' },
    { key: 'session.quote.price', label: 'Quote Price' },
    { key: 'session.quote.cryptoAmount', label: 'Quote Crypto Amount' },
    { key: 'session.quote.fiatAmount', label: 'Quote Fiat Amount' },
    { key: 'session.transaction.id', label: 'Transaction ID' },
    { key: 'session.transaction.status', label: 'Transaction Status' },
    { key: 'session.transaction.txHash', label: 'Transaction Hash' },
    { key: 'session.transaction.executionDate', label: 'Transaction Execution Date' },
    { key: 'sessionEvent.id', label: 'Event ID' },
    { key: 'sessionEvent.type', label: 'Event Status' },
    { key: 'sessionEvent.createdAt', label: 'Event Created At' },
  ],
  rwaClaimed: [
    { key: 'session.rwaId', label: 'RWA ID' },
  ],
  rwaBought: [
    { key: 'session.rwaId', label: 'RWA ID' },
    { key: 'session.amount', label: 'Amount' },
  ],
  tokenClaimed: [
    { key: 'session.tokenId', label: 'Token ID' },
    { key: 'session.amount', label: 'Amount' },
  ],
  tokenBought: [
    { key: 'session.tokenId', label: 'Token ID' },
    { key: 'session.amount', label: 'Amount' },
  ],
  userJoinedEvent: [
    { key: 'session.eventId', label: 'Event ID' },
  ],
};

// Resolves the variables for a node, taking into account dynamic outputs
export function getNodeVariables(node) {
  const nodeType = node.data?.nodeType;
  const output = NODE_OUTPUTS[nodeType];
  if (!output) return null;

  // WalletTwo trigger: base vars + event-specific config fields + extra vars
  if (nodeType === 'trigger_wallettwo') {
    const selectedEvent = node.data?.event;
    const configFields = selectedEvent ? (WALLETTWO_EVENT_FIELDS[selectedEvent] || []) : [];
    const extraVars = selectedEvent ? (WALLETTWO_EXTRA_VARS[selectedEvent] || []) : [];
    const configVars = configFields.map((f) => ({ key: f.key, label: f.label }));
    const seen = new Set(WALLETTWO_BASE_VARS.map((v) => v.key));
    const merged = [...WALLETTWO_BASE_VARS];
    for (const v of [...configVars, ...extraVars]) {
      if (!seen.has(v.key)) { seen.add(v.key); merged.push(v); }
    }
    return { alias: 'trigger', variables: merged };
  }

  // Manual/Schedule trigger: include user-defined custom variables
  if (nodeType === 'trigger_manual' || nodeType === 'trigger_schedule') {
    const customVars = node.data?.customVariables || [];
    return {
      alias: 'trigger',
      variables: [
        ...output.variables,
        ...customVars.map((v) => ({ key: v.key, label: v.label || v.key })),
      ],
    };
  }

  // API Request: dynamic alias from responseName
  // When test results exist, replace generic "data" with discovered response paths
  if (nodeType === 'action_api_request') {
    const name = node.data?.responseName || 'api';
    const testParams = node.data?.lastTestParameters;
    if (Array.isArray(testParams) && testParams.length > 0) {
      return {
        alias: name,
        variables: [
          ...testParams,
          { key: 'status', label: 'HTTP Status' },
        ],
      };
    }
    return {
      alias: name,
      variables: [
        { key: 'data', label: 'Response Data' },
        { key: 'status', label: 'HTTP Status' },
      ],
    };
  }

  return output;
}

export const NODE_OUTPUTS = {
  trigger_schedule: {
    alias: 'trigger',
    variables: [
      { key: 'timestamp', label: 'Trigger Time' },
      { key: 'runCount', label: 'Run Count' },
    ],
  },
  trigger_manual: {
    alias: 'trigger',
    variables: [
      { key: 'timestamp', label: 'Trigger Time' },
      { key: 'triggeredBy', label: 'Triggered By' },
    ],
  },
  trigger_wallettwo: {
    alias: 'trigger',
    variables: [
      { key: 'type', label: 'Event Type' },
      { key: 'timestamp', label: 'Timestamp' },
    ],
  },
  trigger_webhook: {
    alias: 'trigger',
    variables: [
      { key: 'body', label: 'Request Body' },
      { key: 'headers', label: 'Headers' },
      { key: 'method', label: 'HTTP Method' },
    ],
  },
  trigger_onchain: {
    alias: 'trigger',
    variables: [
      { key: 'txHash', label: 'Transaction Hash' },
      { key: 'blockNumber', label: 'Block Number' },
      { key: 'args', label: 'Event Args' },
    ],
  },
  action_api_request: {
    alias: 'api',
    variables: [
      { key: 'data', label: 'Response Data' },
      { key: 'status', label: 'HTTP Status' },
    ],
  },
  action_slack: {
    alias: 'slack',
    variables: [
      { key: 'messageId', label: 'Message ID' },
      { key: 'status', label: 'Status' },
    ],
  },
  action_telegram: {
    alias: 'telegram',
    variables: [
      { key: 'messageId', label: 'Message ID' },
      { key: 'status', label: 'Status' },
    ],
  },
  action_sendgrid: {
    alias: 'sendgrid',
    variables: [
      { key: 'status', label: 'Status' },
      { key: 'messageId', label: 'Message ID' },
    ],
  },
  wallettwo_get_member: {
    alias: 'member',
    variables: [
      { key: 'id', label: 'ID' },
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'role', label: 'Role' },
      { key: 'wallet', label: 'Wallet' },
    ],
  },
  wallettwo_get_members: {
    alias: 'members',
    variables: [
      { key: 'list', label: 'Members List' },
      { key: 'count', label: 'Total Count' },
    ],
  },
  wallet_load: {
    alias: 'wallet',
    variables: [
      { key: 'address', label: 'Wallet Address' },
      { key: 'network', label: 'Network' },
    ],
  },
  wallet_transfer: {
    alias: 'transfer',
    variables: [
      { key: 'txHash', label: 'Transaction Hash' },
      { key: 'status', label: 'Status' },
    ],
  },
  wallet_balance: {
    alias: 'balance',
    variables: [
      { key: 'amount', label: 'Balance' },
      { key: 'token', label: 'Token' },
    ],
  },
  wallet_sign: {
    alias: 'sign',
    variables: [
      { key: 'signature', label: 'Signature' },
    ],
  },
  logic_condition: {
    alias: 'condition',
    variables: [
      { key: 'result', label: 'Condition Result' },
    ],
  },
  logic_delay: {
    alias: 'delay',
    variables: [
      { key: 'completedAt', label: 'Completed At' },
    ],
  },
};
